// Per-image metadata rides along on the image URL as a #fragment, so the DB format
// (plain string arrays), Excel export/import and order snapshots stay unchanged —
// browsers never send the fragment to the server, and old URLs keep working.
// Keys used: `pos` (crop focus), `opt` (the option value this photo represents, e.g.
// "Matte Black") and `step` (the option/step it illustrates, e.g. "Doors Panel Width
// (mm)" — a diagram of which panel that step is about). The storefront swaps to the
// photo when the customer picks that value, or opens that step.

const POS_VALUES = new Set(['top', 'bottom', 'left', 'right'])

// Split "url#pos=top&opt=Matte%20Black" into its base and a {pos, opt} map.
function parseMeta(src) {
  const s = String(src || '')
  const hash = s.indexOf('#')
  if (hash === -1) return { base: s, params: {} }
  const base = s.slice(0, hash)
  const params = {}
  s.slice(hash + 1).split('&').forEach((pair) => {
    if (!pair) return
    const eq = pair.indexOf('=')
    const k = eq === -1 ? pair : pair.slice(0, eq)
    const v = eq === -1 ? '' : pair.slice(eq + 1)
    try { params[k] = decodeURIComponent(v) } catch { params[k] = v }
  })
  return { base, params }
}

function buildMeta(base, params) {
  const frag = Object.entries(params)
    .filter(([, v]) => v !== '' && v != null)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&')
  return frag ? `${base}#${frag}` : base
}

function setParam(src, key, value) {
  const { base, params } = parseMeta(src)
  if (value) params[key] = value
  else delete params[key]
  return buildMeta(base, params)
}

// crop focus — 'top' | 'bottom' | 'left' | 'right' | '' (center/default)
export const imageFocus = (src) => {
  const v = parseMeta(src).params.pos
  return POS_VALUES.has(v) ? v : ''
}
export const withImageFocus = (src, pos) => setParam(src, 'pos', POS_VALUES.has(pos) ? pos : '')

// option value this photo represents — '' when untagged
export const imageOption = (src) => parseMeta(src).params.opt || ''
export const withImageOption = (src, opt) => setParam(src, 'opt', String(opt || '').trim())

// option (step) this photo illustrates — '' when untagged
export const imageStep = (src) => parseMeta(src).params.step || ''
export const withImageStep = (src, step) => setParam(src, 'step', String(step || '').trim())

// style prop for the <img>; undefined when the default centre crop applies
export function focusStyle(src) {
  const pos = imageFocus(src)
  if (!pos) return undefined
  return { objectPosition: pos === 'left' || pos === 'right' ? `${pos} center` : `center ${pos}` }
}
